import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, Chip,
  Divider, IconButton, Tooltip, CircularProgress, Alert
} from '@mui/material';
import {
  ArrowBack, Download, OpenInNew, Archive, PictureAsPdf
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';

// Catches any render error in the detail view so the user sees a message
// instead of a white screen. Logs the stack to the console for debugging.
class DetailErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error('[HistoricalBOEDetail crash]', err, info); }
  render() {
    if (this.state.err) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography fontWeight={700}>Something went wrong while rendering this BOE.</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mt: 1 }}>
              {String(this.state.err?.message || this.state.err)}
            </Typography>
          </Alert>
          <Button variant="contained" startIcon={<ArrowBack />} onClick={() => window.history.back()}>Back</Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

function InfoRow({ label, value, mono = false, highlight = false }) {
  if (!value && value !== 0) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1.5, py: 0.75, borderBottom: '1px solid #f5f5f5', alignItems: 'flex-start' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 170, fontSize: 12.5 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={highlight ? 700 : 500}
        sx={{ fontFamily: mono ? 'monospace' : undefined, fontSize: highlight ? 13 : 12.5,
              color: highlight ? 'primary.main' : undefined, wordBreak: 'break-all' }}>
        {value}
      </Typography>
    </Box>
  );
}

function DocButton({ label, color = 'primary', recId, type, filename }) {
  const openDoc = async (download = false) => {
    try {
      const res = await api.get(`/historical-boe/${recId}/file/${type}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (download) {
        const a = document.createElement('a'); a.href = url; a.download = filename || `${type}.pdf`; a.click();
      } else window.open(url, '_blank');
    } catch { alert('File not available'); }
  };
  return (
    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
      <Tooltip title={`View ${label}`}>
        <Button size="small" variant="outlined" color={color} startIcon={<OpenInNew />}
          onClick={() => openDoc(false)} sx={{ fontSize: 11, py: 0.3 }}>
          {label}
        </Button>
      </Tooltip>
      <Tooltip title={`Download ${label}`}>
        <IconButton size="small" color={color} onClick={() => openDoc(true)}><Download fontSize="small" /></IconButton>
      </Tooltip>
    </Box>
  );
}

function HistoricalBOEDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rec, setRec]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    api.get(`/historical-boe/${id}`)
      .then(r => setRec(r.data?.data || null))
      .catch(e => setError(e.response?.data?.message || e.message || 'Failed to load record'))
      .finally(() => setLoading(false));
  }, [id]);

  const downloadAll = async () => {
    try {
      const res = await api.get(`/historical-boe/${id}/download-all`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BOE${rec?.cbe_number?.replace(/[^a-zA-Z0-9]/g, '') || id}docs.zip`;
      a.click();
    } catch { alert('Failed to download ZIP'); }
  };

  const fmt = (v, d = 2) => v != null ? parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d }) : null;

  const extras = (() => {
    try { return rec?.additional_files ? JSON.parse(rec.additional_files) : []; }
    catch { return []; }
  })();

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (error || !rec) return <Box sx={{ p: 3 }}><Alert severity="error">{error || 'Not found'}</Alert></Box>;

  return (
    <Box>
      <PageHeader
        title={`BOE Detail`}
        subtitle={rec.cbe_number || `Folder #${rec.folder_ref}`}
        breadcrumbs={[{ label: 'Historical BOE', path: '/historical-boe' }, { label: rec.cbe_number || `#${rec.folder_ref}` }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/historical-boe')}>Back</Button>
            <Button variant="contained" startIcon={<Archive />} onClick={downloadAll}>Download All</Button>
          </Box>
        }
      />

      <Grid container spacing={2.5}>
        {/* BOE Fields */}
        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Entry Details</Typography>
              <InfoRow label="CBE Number"         value={rec.cbe_number}        mono highlight />
              <InfoRow label="OOC Issued Date"    value={rec.ooc_date} />
              <InfoRow label="HAWB Number"        value={rec.hawb_number}       mono />
              <InfoRow label="Consignor / Supplier" value={rec.consignor_name} />
              <InfoRow label="Country of Origin"  value={rec.country_of_origin} />
              <InfoRow label="HS Code (CTSH)"     value={rec.hs_code}           mono />
              <InfoRow label="Description"        value={rec.description_of_goods} />
              <InfoRow label="Quantity"           value={rec.quantity != null ? `${fmt(rec.quantity, 0)} ${rec.unit_of_measure || ''}`.trim() : null} />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Financial Details</Typography>
              <InfoRow label="Invoice Number"     value={rec.invoice_number}    mono />
              <InfoRow label="Invoice Value"      value={rec.invoice_value != null ? `${rec.invoice_currency || 'USD'} ${fmt(rec.invoice_value)}` : null} highlight />
              <InfoRow label="Exchange Rate"      value={rec.exchange_rate != null ? `₹ ${parseFloat(rec.exchange_rate).toFixed(4)}` : null} />
              <InfoRow label="Invoice Term"       value={rec.invoice_term} />
              <Divider sx={{ my: 1 }} />
              <InfoRow label="Assessable Value"   value={rec.assessable_value != null ? `₹ ${fmt(rec.assessable_value, 2)}` : null} highlight />
              <InfoRow label="IGST"               value={rec.igst_amount   != null ? `₹ ${fmt(rec.igst_amount, 2)}`   : null} />
              <InfoRow label="Total Customs Duty" value={rec.duty_amount   != null ? `₹ ${fmt(rec.duty_amount, 2)}`   : null} />
              {(() => {
                const av = parseFloat(rec.assessable_value);
                const dv = parseFloat(rec.duty_amount);
                if (!av || !dv || !isFinite(av) || !isFinite(dv)) return null;
                return (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">Effective Duty Rate</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#e65100' }}>
                      {((dv / av) * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>

        {/* Documents */}
        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Documents
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Click to view in browser · ↓ icon to download
              </Typography>

              {rec.boe_path && (
                <DocButton label="BOE Entry" color="primary" recId={id} type="boe"
                  filename={`BOEEntry${rec.cbe_number?.replace(/[^a-zA-Z0-9]/g,'') || id}.pdf`} />
              )}
              {rec.awb_path && (
                <DocButton label="AWB" color="inherit" recId={id} type="awb"
                  filename={`AWB${rec.cbe_number?.replace(/[^a-zA-Z0-9]/g,'') || id}.pdf`} />
              )}
              {rec.invoice_path && (
                <DocButton label="Commercial Invoice" color="success" recId={id} type="invoice"
                  filename={`CI${rec.cbe_number?.replace(/[^a-zA-Z0-9]/g,'') || id}.pdf`} />
              )}
              {rec.rod_path && (
                <DocButton label="FedEx ROD" color="warning" recId={id} type="rod"
                  filename={`ROD${rec.cbe_number?.replace(/[^a-zA-Z0-9]/g,'') || id}.pdf`} />
              )}

              {extras.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Additional Files</Typography>
                  {extras.map((e, i) => (
                    <DocButton key={i}
                      label={e.filename || `Extra ${i + 1}`}
                      color={e.type === 'invoice' ? 'success' : 'secondary'}
                      recId={id} type={`extra_${i}`} filename={e.filename} />
                  ))}
                </>
              )}

              {!rec.boe_path && !rec.awb_path && !rec.invoice_path && !rec.rod_path && extras.length === 0 && (
                <Typography variant="body2" color="text.secondary">No documents available</Typography>
              )}
            </CardContent>
          </Card>

          {/* Quick summary chip strip */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Available Docs</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {rec.boe_path     && <Chip icon={<PictureAsPdf />} label="BOE Entry"          size="small" color="primary"   variant="filled" />}
                {rec.awb_path     && <Chip icon={<PictureAsPdf />} label="AWB"                size="small" variant="outlined" />}
                {rec.invoice_path && <Chip icon={<PictureAsPdf />} label="Comm. Invoice"      size="small" color="success"   variant="outlined" />}
                {rec.rod_path     && <Chip icon={<PictureAsPdf />} label="ROD"                size="small" color="warning"   variant="outlined" />}
                {extras.map((e, i) => (
                  <Chip key={i} icon={<PictureAsPdf />} label={e.type === 'invoice' ? `CI ${i+1}` : e.type === 'awb' ? `AWB ${i+1}` : `Extra ${i+1}`}
                    size="small" color="secondary" variant="outlined" />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Folder ref: #{rec.folder_ref}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default function HistoricalBOEDetail() {
  return (
    <DetailErrorBoundary>
      <HistoricalBOEDetailInner />
    </DetailErrorBoundary>
  );
}
